// Demo mode utilities
import type { UseQueryResult } from '@tanstack/react-query';
import {
    mockAdminUser,
    mockAnalytics,
    mockAppointments,
    mockCourses,
    mockUser,
    mockUsers,
} from './mock-data';

// Check if we're in demo mode
export const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

// Mock query result helper
function createMockQueryResult<T>(data: T): UseQueryResult<T, Error> {
  return {
    data,
    isLoading: false,
    isError: false,
    error: null,
    isSuccess: true,
    status: 'success',
    dataUpdatedAt: Date.now(),
    errorUpdatedAt: 0,
    failureCount: 0,
    failureReason: null,
    errorUpdateCount: 0,
    isFetched: true,
    isFetchedAfterMount: true,
    isFetching: false,
    isRefetching: false,
    isLoadingError: false,
    isPaused: false,
    isPlaceholderData: false,
    isPreviousData: false,
    isRefetchError: false,
    isStale: false,
    refetch: async () =>
      ({
        data,
        isLoading: false,
        isError: false,
        error: null,
        isSuccess: true,
        status: 'success',
      }) as any,
    fetchStatus: 'idle',
  } as UseQueryResult<T, Error>;
}

// Mock API hooks
export const useMockListCourses = () => createMockQueryResult(mockCourses);

export const useMockGetCurrentUser = () => {
  const isAdmin = window.location.pathname.startsWith('/admin');
  return createMockQueryResult(isAdmin ? mockAdminUser : mockUser);
};

export const useMockListAppointments = () =>
  createMockQueryResult(mockAppointments);

export const useMockListUsers = () => createMockQueryResult(mockUsers);

export const useMockGetAnalyticsSummary = () =>
  createMockQueryResult(mockAnalytics.summary);

export const useMockGetRevenueByMonth = () =>
  createMockQueryResult(mockAnalytics.revenueByMonth);

export const useMockGetAppointmentsByStatus = () =>
  createMockQueryResult(mockAnalytics.appointmentsByStatus);

export const useMockGetTrafficByDay = () =>
  createMockQueryResult(mockAnalytics.trafficByDay);

// Mock mutation helper
function createMockMutation<TData, TVariables>() {
  return {
    mutate: (variables: TVariables, options?: any) => {
      setTimeout(() => {
        options?.onSuccess?.({} as TData);
      }, 500);
    },
    mutateAsync: async (variables: TVariables) => {
      return new Promise<TData>(resolve => {
        setTimeout(() => resolve({} as TData), 500);
      });
    },
    isLoading: false,
    isError: false,
    isSuccess: false,
    isIdle: true,
    status: 'idle' as const,
    data: undefined,
    error: null,
    reset: () => {},
    variables: undefined,
    context: undefined,
    failureCount: 0,
    failureReason: null,
    isPaused: false,
    submittedAt: 0,
  };
}

export const useMockCreateAppointment = () => createMockMutation();
export const useMockUpdateAppointment = () => createMockMutation();
export const useMockDeleteAppointment = () => createMockMutation();
export const useMockCreateCourse = () => createMockMutation();
export const useMockUpdateCourse = () => createMockMutation();
export const useMockDeleteCourse = () => createMockMutation();
export const useMockCreateUser = () => createMockMutation();
export const useMockUpdateUser = () => createMockMutation();
export const useMockDeleteUser = () => createMockMutation();
export const useMockLogin = () => createMockMutation();
export const useMockLogout = () => createMockMutation();
